import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import {
  GET_MY_AMCR_ACCOUNT_QUERY,
  UPLOAD_AMCR_FIND_PHOTO_MUTATION,
} from '../../gql';
import { Button, FileWithPreview, ImageDropzone } from '../design-system';
import { AmcrSignInForm } from './AmcrSignInForm';
import { AmcrErrorDetail, getAmcrProblemMessage } from './messages';

/** A file stored in the PAS record, as listed by AMCR. */
export interface AmcrPasFile {
  id: string;
  name: string;
  /** Lowercase hexadecimal SHA-512 of the file bytes. */
  sha512: string;
  uploadedName?: string | null;
}

interface AmcrPasPhotoDialogProps {
  findId: string;
  pasFiles: AmcrPasFile[];
  onClose: () => void;
  onUploaded: (count: number) => void;
}

const SIGN_IN_REQUIRED_PATH = 'amcr.unauthorized';

const photoMessages = defineMessages({
  duplicate: {
    id: 'amcrPas.photos.duplicate',
    defaultMessage: '{file} is already in PAS as {pasFile}.',
  },
});

const contentStyles = { minWidth: { sm: 560 } };
const loadingStyles = { py: 4, alignItems: 'center' };
const listStyles = { m: 0, pl: 2 };
const hiddenStyles = { display: 'none' };
const storedNameStyles = { color: 'text.secondary' };

const getPasFileName = (file: AmcrPasFile) => file.uploadedName || file.name;

const toSha512Hex = async (file: File) => {
  const digest = await crypto.subtle.digest(
    'SHA-512',
    await file.arrayBuffer()
  );

  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0')
  ).join('');
};

/** Hashing needs a secure context; without one the check is skipped. */
const findStoredCopy = async (file: File, pasFiles: AmcrPasFile[]) => {
  try {
    const sha512 = await toSha512Hex(file);

    return pasFiles.find((pasFile) => pasFile.sha512 === sha512);
  } catch {
    return undefined;
  }
};

const PasFileList = ({ pasFiles }: { pasFiles: AmcrPasFile[] }) => {
  const countValues = { count: pasFiles.length };

  return (
    <Stack gap={0.5}>
      <Typography variant="body2">
        <FormattedMessage
          id="amcrPas.photos.count"
          defaultMessage="{count, plural, =0 {The find has no photos in PAS yet.} one {The find has # photo in PAS:} other {The find has # photos in PAS:}}"
          values={countValues}
        />
      </Typography>
      {pasFiles.length > 0 && (
        <Stack component="ul" sx={listStyles}>
          {pasFiles.map((pasFile) => (
            <Typography component="li" variant="body2" key={pasFile.id}>
              {getPasFileName(pasFile)}
              {pasFile.uploadedName && (
                <Box component="span" sx={storedNameStyles}>
                  {` (${pasFile.name})`}
                </Box>
              )}
            </Typography>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

const AmcrPasPhotoDialog = ({
  findId,
  pasFiles,
  onClose,
  onUploaded,
}: AmcrPasPhotoDialogProps) => {
  const intl = useIntl();
  const [selectedFiles, setSelectedFiles] = useState<FileWithPreview[]>([]);
  const [storedCopies, setStoredCopies] = useState<Map<File, AmcrPasFile>>(
    () => new Map()
  );
  const [isSignInRequired, setIsSignInRequired] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [errors, setErrors] = useState<AmcrErrorDetail[]>([]);

  const accountQuery = useQuery(GET_MY_AMCR_ACCOUNT_QUERY, {
    fetchPolicy: 'network-only',
  });
  const account = accountQuery.data?.getMyAmcrAccount;
  const hasValidAccount = Boolean(account && !account.expired);
  const isSignedIn = hasValidAccount && !isSignInRequired;

  const [uploadAmcrFindPhoto] = useMutation(UPLOAD_AMCR_FIND_PHOTO_MUTATION);

  const duplicates = selectedFiles.flatMap((file) => {
    const storedCopy = storedCopies.get(file);

    return storedCopy ? [{ file, storedCopy }] : [];
  });

  const uploadPhoto = async (file: File): Promise<AmcrErrorDetail[]> => {
    try {
      const { data } = await uploadAmcrFindPhoto({
        variables: { findId, file },
      });
      const result = data?.uploadAmcrFindPhoto;

      return result?.find ? [] : result?.errors || [];
    } catch {
      return [
        {
          path: 'amcr',
          error: intl.formatMessage({
            id: 'amcrPas.photos.failed',
            defaultMessage: 'The photo could not be uploaded to PAS.',
          }),
        },
      ];
    }
  };

  const handleFilesAccept = async (acceptedFiles: File[]) => {
    const matches = await Promise.all(
      acceptedFiles.map(
        async (file) => [file, await findStoredCopy(file, pasFiles)] as const
      )
    );

    setStoredCopies((previous) => {
      const next = new Map(previous);

      matches.forEach(([file, storedCopy]) => {
        if (storedCopy) {
          next.set(file, storedCopy);
        }
      });

      return next;
    });
  };

  // Sequential on purpose: AMCR locks the record during each upload.
  const handleUpload = async () => {
    setErrors([]);
    setUploading(true);

    let remaining = selectedFiles;
    let uploaded = uploadedCount;

    for (const file of selectedFiles) {
      const uploadErrors = await uploadPhoto(file);

      if (uploadErrors.length > 0) {
        setUploading(false);
        setUploadedCount(uploaded);

        if (uploadErrors.some(({ path }) => path === SIGN_IN_REQUIRED_PATH)) {
          setIsSignInRequired(true);
          return;
        }

        setErrors(uploadErrors);
        return;
      }

      // Removed right away so a retry never attaches the same photo twice.
      remaining = remaining.filter((candidate) => candidate !== file);
      uploaded += 1;
      setSelectedFiles(remaining);
    }

    setUploading(false);
    onUploaded(uploaded);
    onClose();
  };

  const handleSignedIn = () => {
    setIsSignInRequired(false);

    if (selectedFiles.length > 0) {
      handleUpload();
    }
  };

  const handleClose = () => {
    if (uploading) {
      return;
    }

    if (uploadedCount > 0) {
      onUploaded(uploadedCount);
    }

    onClose();
  };

  // Stays mounted while re-signing in so the selected photos are kept.
  const renderUploadForm = () => (
    <Box sx={isSignedIn ? undefined : hiddenStyles}>
      <DialogContent sx={contentStyles}>
        <Stack gap={2}>
          <PasFileList pasFiles={pasFiles} />
          <ImageDropzone
            files={selectedFiles}
            setFiles={setSelectedFiles}
            onFilesAccept={handleFilesAccept}
          />
          {duplicates.length > 0 && (
            <Alert severity="warning" variant="outlined">
              <Stack component="ul" sx={listStyles}>
                {duplicates.map(({ file, storedCopy }) => (
                  <li key={`${file.name}-${file.size}`}>
                    {intl.formatMessage(photoMessages.duplicate, {
                      file: file.name,
                      pasFile: getPasFileName(storedCopy),
                    })}
                  </li>
                ))}
              </Stack>
            </Alert>
          )}
          {errors.length > 0 && (
            <Alert severity="error" variant="outlined">
              <Stack component="ul" sx={listStyles}>
                {errors.map((detail) => (
                  <li key={`${detail.path}:${detail.error}`}>
                    {getAmcrProblemMessage(intl, detail)}
                  </li>
                ))}
              </Stack>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button tone="neutral" disabled={uploading} onClick={handleClose}>
          <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
        </Button>
        <Button
          disabled={uploading || selectedFiles.length === 0}
          onClick={handleUpload}
        >
          {uploading ? (
            <FormattedMessage
              id="amcrPas.photos.uploading"
              defaultMessage="Uploading…"
            />
          ) : (
            <FormattedMessage
              id="amcrPas.photos.submit"
              defaultMessage="Upload"
            />
          )}
        </Button>
      </DialogActions>
    </Box>
  );

  return (
    <Dialog open maxWidth="md" onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage
          id="amcrPas.photos.title"
          defaultMessage="Add PAS photos"
        />
      </DialogTitle>
      {accountQuery.loading && (
        <DialogContent sx={contentStyles}>
          <Stack sx={loadingStyles}>
            <CircularProgress size={32} />
          </Stack>
        </DialogContent>
      )}
      {!accountQuery.loading && !isSignedIn && (
        <AmcrSignInForm
          isSessionExpired={Boolean(account) || isSignInRequired}
          onCancel={handleClose}
          onSignedIn={handleSignedIn}
        />
      )}
      {!accountQuery.loading && hasValidAccount && renderUploadForm()}
    </Dialog>
  );
};

export { AmcrPasPhotoDialog };
